import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listContactMessages } from '../api/contacts';
import ContactMetaMessaging from './ContactMetaMessaging';
import MessageBubble from './MessageBubble';
import { getApiErrorMessage } from '../utils/errors';

const MESSAGES_PER_PAGE = 50;
const REFRESH_INTERVAL_MS = 10000;

function formatDateTime(iso) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export default function ContactConversationPanel({
  contactId,
  contact,
  onReleaseHandoff,
  releasing = false,
  onSent,
}) {
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState('');
  const [newInboundCount, setNewInboundCount] = useState(0);
  const threadRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const knownIdsRef = useRef(new Set());
  const hasThreadContentRef = useRef(false);

  const activeSession = useMemo(
    () => (contact?.sessions || []).find((session) => session.status === 'active'),
    [contact]
  );
  const activeSessionId = activeSession?.id || null;
  const isPaused = contact?.status === 'paused';

  const fetchPage = useCallback(
    (targetPage) =>
      listContactMessages(contactId, {
        page: targetPage,
        perPage: MESSAGES_PER_PAGE,
        sessionId: activeSessionId,
      }),
    [contactId, activeSessionId]
  );

  const loadLatest = useCallback(
    async ({ silent = false } = {}) => {
      if (!activeSessionId) {
        if (!hasThreadContentRef.current) {
          setMessages([]);
          setTotal(0);
          setPage(1);
        }
        return;
      }

      if (!silent) {
        setLoading(true);
        setError('');
      }
      try {
        const firstPage = await fetchPage(1);
        const totalCount = firstPage.meta?.total ?? (firstPage.data || []).length;
        const lastPage = Math.max(1, Math.ceil(totalCount / MESSAGES_PER_PAGE));
        const latest = lastPage === 1 ? firstPage : await fetchPage(lastPage);
        const latestMessages = latest.data || [];

        const arrivedFromContact = latestMessages.filter(
          (message) => message.role === 'user' && !knownIdsRef.current.has(message.id)
        ).length;
        const firstLoad = knownIdsRef.current.size === 0;
        knownIdsRef.current = new Set(latestMessages.map((message) => message.id));

        stickToBottomRef.current = true;
        setMessages(latestMessages);
        hasThreadContentRef.current = latestMessages.length > 0;
        setTotal(latest.meta?.total ?? totalCount);
        setPage(lastPage);
        setNewInboundCount((current) =>
          silent && !firstLoad ? current + arrivedFromContact : 0
        );
        if (!silent) setError('');
      } catch (err) {
        if (!silent) {
          setError(getApiErrorMessage(err, 'Não foi possível carregar a conversa.'));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [activeSessionId, fetchPage]
  );

  useEffect(() => {
    knownIdsRef.current = new Set();
    setNewInboundCount(0);
    hasThreadContentRef.current = false;
    setMessages([]);
    setTotal(0);
    setPage(1);
  }, [contactId]);

  useEffect(() => {
    // After a send, local bubbles already exist — refresh without a loading flash.
    loadLatest({ silent: hasThreadContentRef.current });
  }, [loadLatest]);

  useEffect(() => {
    if (!activeSessionId) return undefined;

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadLatest({ silent: true });
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [activeSessionId, loadLatest]);

  useEffect(() => {
    if (!stickToBottomRef.current || !threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const handleLoadOlder = async () => {
    if (page <= 1 || loadingOlder) return;

    setLoadingOlder(true);
    setError('');
    try {
      const previous = await fetchPage(page - 1);
      stickToBottomRef.current = false;
      setMessages((current) => [...(previous.data || []), ...current]);
      setPage(page - 1);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar mensagens anteriores.'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSent = async (response) => {
    const sentMessage = response?.message;
    if (sentMessage) {
      stickToBottomRef.current = true;
      knownIdsRef.current.add(sentMessage.id);
      hasThreadContentRef.current = true;
      setMessages((current) =>
        current.some((message) => message.id === sentMessage.id)
          ? current
          : [...current, sentMessage]
      );
      setTotal((current) => current + 1);
    }
    if (onSent) await onSent(response);
  };

  const handleGoToNewMessages = () => {
    setNewInboundCount(0);
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  };

  const hasOlderMessages = page > 1;

  return (
    <section className="surface-card overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Conversa</h2>
          <p className="mt-1 text-xs text-content-muted">
            {activeSession
              ? `Sessão ativa desde ${formatDateTime(activeSession.started_at)}`
              : 'Nenhuma sessão ativa — o próximo envio inicia uma conversa'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isPaused && onReleaseHandoff && (
            <button
              type="button"
              onClick={onReleaseHandoff}
              disabled={releasing}
              className="btn-secondary px-3 py-2 text-sm disabled:opacity-40"
            >
              {releasing ? 'Liberando…' : 'Devolver para a IA'}
            </button>
          )}
          <button
            type="button"
            onClick={() => loadLatest()}
            disabled={loading || !activeSessionId}
            className="text-xs text-forge-600 hover:text-forge-800 disabled:opacity-40"
          >
            {loading ? 'Atualizando…' : 'Atualizar conversa'}
          </button>
        </div>
      </div>

      <div
        className={`px-5 py-3 text-xs ${
          isPaused ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'
        }`}
      >
        <span className="font-medium">
          {isPaused ? 'Atendimento humano ativo — IA pausada. ' : 'Ao enviar, você assume o atendimento. '}
        </span>
        {isPaused
          ? 'O clone não responderá este contato até você devolver a conversa.'
          : 'A IA será pausada para não misturar respostas automáticas com o atendimento humano.'}
      </div>

      {error && (
        <div className="border-b border-red-200 bg-red-50 px-5 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div ref={threadRef} className="max-h-[26rem] space-y-2 overflow-y-auto bg-slate-50/60 px-5 py-4">
        {hasOlderMessages && (
          <div className="flex justify-center pb-1">
            <button
              type="button"
              onClick={handleLoadOlder}
              disabled={loadingOlder}
              className="text-xs text-forge-600 hover:text-forge-800 disabled:opacity-40"
            >
              {loadingOlder ? 'Carregando…' : 'Carregar mensagens anteriores'}
            </button>
          </div>
        )}

        {loading && messages.length === 0 && (
          <p className="py-6 text-center text-sm text-content-muted">Carregando conversa…</p>
        )}

        {!loading && messages.length === 0 && (
          <p className="py-6 text-center text-sm text-content-subtle italic">
            {activeSessionId
              ? 'Nenhuma mensagem nesta conversa.'
              : 'Envie a primeira mensagem para iniciar a conversa.'}
          </p>
        )}

        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
      </div>

      {newInboundCount > 0 && (
        <div className="flex justify-center bg-slate-50/60 px-5 pb-3">
          <button
            type="button"
            onClick={handleGoToNewMessages}
            className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-200"
          >
            {newInboundCount} nova{newInboundCount !== 1 ? 's' : ''} mensagem
            {newInboundCount !== 1 ? 's' : ''} do contato
          </button>
        </div>
      )}

      <div className="border-t border-slate-200 px-5 py-4">
        <ContactMetaMessaging contactId={contactId} onSent={handleSent} />
        {total > 0 && (
          <p className="mt-2 text-[11px] text-content-subtle">
            {total} mensagem{total !== 1 ? 's' : ''} nesta sessão
          </p>
        )}
      </div>
    </section>
  );
}
